import { useId } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Info } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { TokenUsageCopy, TokenUsageReport } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import DateRangeFilter from './DateRangeFilter'
import { formatUsageDate } from './dateRangePresets'
import NextUsageUpdate from './NextUsageUpdate'

interface Props {
  workerId: string
  report: TokenUsageReport
  copy: TokenUsageCopy
}

function chartCeiling(maximum: number) {
  if (maximum <= 1) return 1
  if (maximum <= 5) return Math.ceil(maximum)
  if (maximum < 20) return Math.ceil(maximum / 5) * 5
  if (maximum < 80) return Math.ceil(maximum / 10) * 10
  return Math.ceil(Math.max(maximum, 100) / 25) * 25
}

export default function UsageTimeline({ workerId, report, copy }: Props) {
  const reduceMotion = useReducedMotion()
  const gradientId = useId().replace(/:/g, '')
  const maxUsage = Math.max(...report.timeline.map((item) => item.percentage ?? 0), 0)
  const domainMax = chartCeiling(maxUsage)
  const hasPartialCoverage = Boolean(report.available_from && report.date_from && report.date_from < report.available_from)

  return (
    <div className="p-4 md:p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-[14px] font-semibold text-foreground">{copy.timeline_title}</h2>
          <p className="mt-1 text-[12px] text-muted-foreground">{copy.timeline_description}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <DateRangeFilter workerId={workerId} report={report} copy={copy} />
          <NextUsageUpdate nextUpdateAt={report.next_update_at} label={copy.next_update} />
        </div>
      </div>
      {hasPartialCoverage && report.available_from && (
        <div role="status" className="mb-4 flex items-start gap-2 rounded-lg border border-warning/25 bg-warning/10 px-3 py-2 text-[11px] leading-relaxed text-warning">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>{copy.coverage_warning} <strong>{formatUsageDate(report.available_from)}</strong>.</span>
        </div>
      )}

      <div className="h-[210px] w-full" role="img" aria-label={copy.timeline_description}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart accessibilityLayer data={report.timeline} margin={{ top: 10, right: 4, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--agent)" stopOpacity={1} />
                <stop offset="100%" stopColor="var(--agent)" stopOpacity={0.58} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis
              dataKey="date_label"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'var(--faint-foreground)', fontSize: 10.5 }}
              dy={8}
            />
            <YAxis
              domain={[0, domainMax]}
              axisLine={false}
              tickLine={false}
              width={42}
              tick={{ fill: 'var(--faint-foreground)', fontSize: 10.5 }}
              tickFormatter={(value: number) => `${value}%`}
            />
            {domainMax >= 100 && <ReferenceLine y={100} stroke="var(--agent)" strokeDasharray="4 5" strokeOpacity={0.32} />}
            <Tooltip
              cursor={{ fill: 'var(--agent-soft)' }}
              content={({ active, payload, label }) => active && payload?.[0] ? (
                <motion.div
                  initial={reduceMotion ? false : { opacity: 0, y: 2 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.12 }}
                  className="rounded-lg border border-border bg-popover px-3 py-2 shadow-md"
                >
                  <p className="text-[10.5px] text-faint-foreground">{label}</p>
                  <p className="mt-1 text-[12px] font-medium tabular-nums text-foreground">
                    {String(payload[0].payload.percentage_label ?? '0')}%
                  </p>
                </motion.div>
              ) : null}
            />
            <Bar
              dataKey="percentage"
              fill={`url(#${gradientId})`}
              radius={[5, 5, 1, 1]}
              maxBarSize={34}
              activeBar={{ fill: 'var(--agent)', opacity: 1 }}
              isAnimationActive={!reduceMotion}
              animationDuration={580}
              animationEasing="ease-out"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
